import { useEffect, useMemo, useState } from 'react';

import { PaginationControls } from '../common/pagination-controls';

interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  paymentMethod: string;
  subscriptionStart?: string;
  subscriptionEnd?: string;
}

interface TransactionHistoryProps {
  transactions: Transaction[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);

export function TransactionHistory({ transactions }: TransactionHistoryProps) {
  const itemsPerPage = 5;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(transactions.length / itemsPerPage));
  const pagedTransactions = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return transactions.slice(start, start + itemsPerPage);
  }, [page, transactions]);

  useEffect(() => {
    setPage(1);
  }, [transactions.length]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const getStatusBadge = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-600 text-white';
      case 'pending':
        return 'bg-goldBase text-blackBase';
      case 'failed':
        return 'bg-redBase text-white';
      default:
        return 'bg-greyWithOpacity30 text-grey2';
    }
  };

  const getStatusLabel = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return 'Оплачено';
      case 'pending':
        return 'В обработке';
      case 'failed':
        return 'Ошибка';
      default:
        return '—';
    }
  };

  return (
    <div className="rounded-[24px] bg-white border border-[#EFEFEF] shadow-[0px_10px_30px_rgba(0,0,0,0.05)] p-6">
      <h3 className="font-comfortaa mb-6">История платежей</h3>
      <div className="space-y-4">
        {pagedTransactions.map((transaction) => (
          <div
            key={transaction.id}
            className="p-4 border border-[#EFEFEF] rounded-[16px] hover:bg-grey5 transition-colors"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h4 className="font-comfortaa text-base">{transaction.description}</h4>
                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadge(transaction.status)}`}
                  >
                    {getStatusLabel(transaction.status)}
                  </span>
                </div>
                <div className="flex flex-col gap-2 text-sm text-grey2">
                  {transaction.subscriptionStart && transaction.subscriptionEnd && (
                    <div className="flex items-center gap-2 text-grey1">
                      <SubscriptionIcon />
                      <span>
                        Активно: {transaction.subscriptionStart} — {transaction.subscriptionEnd}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <CalendarIcon />
                      <span>{transaction.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CardIcon />
                      <span>{transaction.paymentMethod}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-semibold text-blackBase mb-3">{formatCurrency(transaction.amount)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {transactions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-grey1">Платежей пока нет</p>
        </div>
      )}

      {transactions.length > itemsPerPage && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          keyPrefix="tx-page"
          maxVisiblePages={5}
        />
      )}
    </div>
  );
}

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-4" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-4" aria-hidden="true">
    <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SubscriptionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-4" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7 2v4M17 2v4M3 9h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 13h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
